import { TRIAL_LENGTHS, UPGRADES } from './balance';
import { newGame, type GameState } from './state';

/**
 * Versioned save format. Bump SAVE_VERSION and add a migration whenever GameState changes
 * shape, so players never lose progress across releases.
 */

export const SAVE_VERSION = 1;

interface SaveEnvelope {
  v: number;
  savedAt: number;
  state: unknown;
}

type Migration = (state: Record<string, unknown>) => Record<string, unknown>;

/** MIGRATIONS[k] upgrades a version-k save to version k+1. */
const MIGRATIONS: Record<number, Migration> = {};

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const int = (x: unknown, fallback: number) => (isNum(x) ? Math.trunc(x) : fallback);
const nonNeg = (x: unknown, fallback: number) => Math.max(0, int(x, fallback));

/** Coerces untrusted data into a valid GameState, falling back to defaults field by field. */
export function sanitize(raw: unknown): GameState {
  const d = newGame();
  if (typeof raw !== 'object' || raw === null) return d;
  const r = raw as Record<string, unknown>;

  const upgradesIn = (typeof r.upgrades === 'object' && r.upgrades) || {};
  const upgrades = { ...d.upgrades };
  for (const u of UPGRADES) {
    const lv = nonNeg((upgradesIn as Record<string, unknown>)[u.id], 0);
    upgrades[u.id] = Math.min(lv, u.costs.length);
  }

  const bestsIn = (typeof r.trialBests === 'object' && r.trialBests) || {};
  const trialBests = { ...d.trialBests };
  for (const t of TRIAL_LENGTHS) {
    trialBests[t] = nonNeg((bestsIn as Record<string, unknown>)[t], 0);
  }

  const minVisited = Math.min(0, int(r.minVisited, 0));
  const maxVisited = Math.max(0, int(r.maxVisited, 0));
  const position = Math.min(maxVisited, Math.max(minVisited, int(r.position, 0)));

  return {
    position,
    direction: r.direction === -1 && upgrades.inverter > 0 ? -1 : 1,
    minVisited,
    maxVisited,
    points: nonNeg(r.points, 0),
    lifetimePoints: nonNeg(r.lifetimePoints, 0),
    steps: nonNeg(r.steps, 0),
    taps: nonNeg(r.taps, 0),
    upgrades,
    trialBests,
    playTimeMs: nonNeg(r.playTimeMs, 0),
    startedAt: nonNeg(r.startedAt, d.startedAt),
  };
}

export function serialize(state: GameState, now = Date.now()): string {
  const envelope: SaveEnvelope = { v: SAVE_VERSION, savedAt: now, state };
  return JSON.stringify(envelope);
}

/** Parses a save string. Returns null for anything unreadable or from a newer version. */
export function deserialize(text: string): GameState | null {
  let env: SaveEnvelope;
  try {
    env = JSON.parse(text) as SaveEnvelope;
  } catch {
    return null;
  }
  if (typeof env !== 'object' || env === null || !isNum(env.v) || env.v > SAVE_VERSION) {
    return null;
  }
  let state = (env.state ?? {}) as Record<string, unknown>;
  for (let v = env.v; v < SAVE_VERSION; v++) {
    const migrate = MIGRATIONS[v];
    if (migrate) state = migrate(state);
  }
  return sanitize(state);
}

/** Portable save code for export/import (base64 of the JSON envelope). */
export function toSaveCode(state: GameState): string {
  return btoa(unescape(encodeURIComponent(serialize(state))));
}

export function fromSaveCode(code: string): GameState | null {
  try {
    return deserialize(decodeURIComponent(escape(atob(code.trim()))));
  } catch {
    return null;
  }
}
